import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import adminApi from '../../services/adminApi';
import LoadingSpinner from '../../components/LoadingSpinner';
import { 
  IoPeopleOutline, 
  IoPersonOutline, 
  IoSearchOutline,
  IoEyeOutline,
  IoCalendarOutline,
  IoKeyOutline,
  IoChevronDownOutline,
  IoChevronUpOutline
} from 'react-icons/io5';
import './AdminFamiliesPage.css';

const AdminFamiliesPage = () => {
  const { user } = useAuth();
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  
  // Filters and search
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [currentPage, setCurrentPage] = useState(1);
  
  // Expanded family details
  const [expandedFamily, setExpandedFamily] = useState(null);
  const [familyDetails, setFamilyDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});

  useEffect(() => {
    if (user?.isAdmin) {
      loadFamilies();
    }
  }, [user, currentPage, search, sortBy, sortOrder]);

  const loadFamilies = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/admin/families', {
        params: {
          page: currentPage,
          limit: 20,
          search,
          sortBy,
          sortOrder
        }
      });
      
      setFamilies(response.data.families || []);
      setPagination(response.data.pagination || {});
    } catch (error) {
      console.error('Load families error:', error);
      setError('Failed to load families');
    } finally {
      setLoading(false);
    }
  };

  const loadFamilyDetails = async (familyId) => {
    if (familyDetails[familyId]) {
      return; // Already loaded
    }

    try {
      setLoadingDetails(prev => ({ ...prev, [familyId]: true }));
      const response = await adminApi.get(`/admin/families/${familyId}`);
      setFamilyDetails(prev => ({
        ...prev,
        [familyId]: response.data.family
      }));
    } catch (error) {
      console.error('Load family details error:', error);
    } finally {
      setLoadingDetails(prev => ({ ...prev, [familyId]: false }));
    }
  };

  const handleExpandFamily = async (familyId) => {
    if (expandedFamily === familyId) {
      setExpandedFamily(null);
    } else {
      setExpandedFamily(familyId);
      await loadFamilyDetails(familyId);
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('DESC');
    }
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!user?.isAdmin) {
    return (
      <div className="admin-access-denied">
        <h1>Access Denied</h1>
        <p>You don't have permission to access this page.</p>
      </div>
    );
  }

  if (loading && families.length === 0) {
    return (
      <div className="admin-loading">
        <LoadingSpinner size="large" message="Loading families..." />
      </div>
    );
  }

  return (
    <div className="admin-families-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Family Groups</h1>
        <p className="admin-page-description">
          Manage and view all family groups, their members, and readers
        </p>
      </div>

      {/* Search and Filters */}
      <div className="admin-card">
        <div className="families-controls">
          <div className="search-box">
            <IoSearchOutline className="search-icon" />
            <input
              type="text"
              placeholder="Search families by name or invite code..."
              value={search}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
          
          <div className="sort-controls">
            <label>Sort by:</label>
            <select 
              value={sortBy} 
              onChange={(e) => handleSortChange(e.target.value)}
              className="sort-select"
            >
              <option value="createdAt">Date Created</option>
              <option value="name">Name</option>
              <option value="memberCount">Member Count</option>
              <option value="readerCount">Reader Count</option>
            </select>
            <button 
              onClick={() => setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')}
              className="sort-order-btn"
            >
              {sortOrder === 'ASC' ? '↑' : '↓'}
            </button>
          </div>
        </div>
      </div>

      {/* Families List */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">
            Families ({pagination.totalFamilies || 0})
          </h2>
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
            <button onClick={loadFamilies} className="retry-btn">
              Try Again
            </button>
          </div>
        )}

        {families.length === 0 && !loading ? (
          <div className="no-families">
            <IoPeopleOutline className="no-families-icon" />
            <h3>No families found</h3>
            <p>No family groups match your search criteria.</p>
          </div>
        ) : (
          <div className="families-list">
            {families.map((family) => (
              <div key={family.id} className="family-item">
                <div className="family-header" onClick={() => handleExpandFamily(family.id)}>
                  <div className="family-info">
                    <h3 className="family-name">{family.name}</h3>
                    <div className="family-meta">
                      <span className="family-stats">
                        <IoPeopleOutline /> {family.memberCount} members
                      </span>
                      <span className="family-stats">
                        <IoPersonOutline /> {family.activeReaderCount} active readers
                      </span>
                      <span className="family-stats">
                        <IoCalendarOutline /> Created {formatDate(family.createdAt)}
                      </span>
                      <span className="family-stats">
                        <IoKeyOutline /> {family.inviteCode}
                      </span>
                    </div>
                  </div>
                  <div className="expand-btn">
                    {expandedFamily === family.id ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
                  </div>
                </div>

                {expandedFamily === family.id && (
                  <div className="family-details">
                    {loadingDetails[family.id] ? (
                      <div className="details-loading">
                        <LoadingSpinner size="small" message="Loading details..." />
                      </div>
                    ) : familyDetails[family.id] ? (
                      <FamilyDetailsView family={familyDetails[family.id]} />
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              className="pagination-btn"
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={!pagination.hasNextPage}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const FamilyDetailsView = ({ family }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="family-details-content">
      <div className="details-section">
        <h4>Family Information</h4>
        <div className="details-grid">
          <div className="detail-item">
            <label>Name:</label>
            <span>{family.name}</span>
          </div>
          <div className="detail-item">
            <label>Invite Code:</label>
            <span className="invite-code">{family.inviteCode}</span>
          </div>
          <div className="detail-item">
            <label>Created:</label>
            <span>{formatDate(family.createdAt)}</span>
          </div>
          <div className="detail-item">
            <label>Creator:</label>
            <span>{family.creator?.name} ({family.creator?.email})</span>
          </div>
          <div className="detail-item">
            <label>Max Members:</label>
            <span>{family.maxMembers}</span>
          </div>
          <div className="detail-item">
            <label>Active:</label>
            <span className={family.isActive ? 'status-active' : 'status-inactive'}>
              {family.isActive ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      </div>

      <div className="details-section">
        <h4>Members ({family.memberCount})</h4>
        <div className="members-list">
          {family.members?.map((member) => (
            <div key={member.id} className="member-item">
              <div className="member-info">
                <strong>{member.name}</strong>
                <span className="member-email">{member.email}</span>
              </div>
              <div className="member-details">
                <span className="member-setting">
                  Competitive: {member.competitiveMode ? 'On' : 'Off'}
                </span>
                <span className="member-setting">
                  Bible: {member.bibleTranslation}
                </span>
                <span className="member-setting">
                  Joined: {formatDate(member.createdAt)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="details-section">
        <h4>Readers ({family.readerCount})</h4>
        <div className="readers-list">
          {family.readers?.map((reader) => (
            <div key={reader.id} className="reader-item">
              <div className="reader-info">
                <strong>{reader.name}</strong>
                {reader.isPrimary && <span className="primary-badge">Primary</span>}
                <span className="reader-owner">({reader.user?.name})</span>
              </div>
              <div className="reader-details">
                <span className="reader-stat">
                  Age: {reader.age || 'Not set'}
                </span>
                <span className="reader-stat">
                  Gender: {reader.gender || 'Not set'}
                </span>
                <span className="reader-stat">
                  Reports: {reader.reports?.length || 0}
                </span>
                <span className={`reader-status ${reader.isActive ? 'active' : 'inactive'}`}>
                  {reader.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="details-section">
        <h4>Statistics</h4>
        <div className="stats-grid">
          <div className="stat-item">
            <label>Total Members:</label>
            <span>{family.memberCount}</span>
          </div>
          <div className="stat-item">
            <label>Total Readers:</label>
            <span>{family.readerCount}</span>
          </div>
          <div className="stat-item">
            <label>Active Readers:</label>
            <span>{family.activeReaderCount}</span>
          </div>
          <div className="stat-item">
            <label>Total Reports:</label>
            <span>{family.totalReports}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminFamiliesPage;
